import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  X, 
  Plus,
  Calculator,
  BookOpen,
  Lightbulb,
  Star,
  Clock
} from 'lucide-react';
import { clsx } from 'clsx';
import { useSolutionStore } from '../../stores/solutionStore';
import SolutionCard from './SolutionCard';
import SolutionFilters from './SolutionFilters';

const SolutionSidebar = ({ 
  isOpen, 
  onClose, 
  onInsertSolution,
  currentQuestion // The question being edited
}) => {
  const {
    solutions,
    isLoading,
    hasMore,
    filters,
    loadSolutions,
    loadCategories,
    setFilters
  } = useSolutionStore();

  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      loadSolutions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadSolutions();
    }
  }, [filters]);

  const handleSearch = (searchTerm) => {
    setFilters({ searchTerm });
  };

  const categories = [
    { id: 'all', label: 'All Solutions', icon: BookOpen },
    { id: 'formulas', label: 'Formulas', icon: Calculator },
    { id: 'examples', label: 'Worked Examples', icon: Lightbulb },
    { id: 'theorems', label: 'Theorems', icon: BookOpen },
    { id: 'popular', label: 'Popular', icon: Star }
  ];

  const getFilteredSolutions = () => {
    if (selectedCategory === 'all') return solutions;
    if (selectedCategory === 'popular') {
      return [...solutions].sort((a, b) => (b.views || 0) - (a.views || 0));
    }
    return solutions.filter(solution => solution.type === selectedCategory);
  };

  const filteredSolutions = getFilteredSolutions();

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={clsx(
        'fixed top-0 right-0 h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50',
        'flex flex-col',
        isOpen ? 'translate-x-0' : 'translate-x-full'
      )}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-sigma-50">
          <div className="flex items-center space-x-2">
            <Calculator className="w-5 h-5 text-sigma-600" />
            <h2 className="text-lg font-semibold text-gray-900">Mathematical Solutions</h2>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                showFilters 
                  ? 'bg-sigma-100 text-sigma-700' 
                  : 'text-gray-500 hover:bg-gray-100'
              )}
            >
              <Filter className="w-4 h-4" />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Current Question Context */}
        {currentQuestion && (
          <div className="p-4 bg-blue-50 border-b border-blue-200">
            <div className="text-sm">
              <div className="font-medium text-blue-900">Adding solution to:</div>
              <div className="text-blue-700 truncate">{currentQuestion.title || 'Untitled Question'}</div>
              <div className="text-blue-600 text-xs">
                {currentQuestion.type === 'math' ? 'Mathematical Question' : 'Text Question'}
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search solutions..."
              value={filters.searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500 text-sm"
            />
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="border-b border-gray-200">
            <SolutionFilters />
          </div>
        )}

        {/* Categories */}
        <div className="p-4 border-b border-gray-200">
          <div className="grid grid-cols-1 gap-2">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={clsx(
                    'flex items-center p-2 rounded-lg text-sm font-medium transition-colors',
                    selectedCategory === category.id
                      ? 'bg-sigma-100 text-sigma-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  )}
                >
                  <IconComponent className="w-4 h-4 mr-2" />
                  <span>{category.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Solutions List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && solutions.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sigma-600"></div>
            </div>
          ) : filteredSolutions.length > 0 ? (
            <div className="p-4 space-y-4">
              {filteredSolutions.map((solution) => (
                <SolutionCard
                  key={solution.id}
                  solution={solution}
                  onInsert={() => onInsertSolution(solution)}
                  compact={true}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 p-4 text-center">
              <Calculator className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-gray-500 text-sm">No solutions found</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SolutionSidebar;